/* eslint-disable camelcase */
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { useDeletePlotOverview, usePatchPlotOverview, usePostPlotOverview } from '@/api/rest/resources/plot';
import {
  GET_PLOT_OVERVIEW_PATH,
  GetPlotOverviewErrorDto,
  GetPlotOverviewRequestQueryParams,
  GetPlotOverviewSuccessDto,
  PatchPlotOverviewRequestBodyData,
  PlotOverview,
  PostPlotOverviewRequestBodyData,
} from '@/api/rest/resources/types/plot';
import { usePlotId } from '@/pages/shared/hooks/usePlotId';
import { assertIsDefined } from '@/utils/assert';

type SuccessDto = GetPlotOverviewSuccessDto;
type ErrorDto = GetPlotOverviewErrorDto;

const PLOT_OVERVIEW_QUERY_KEY = ['plot', 'plot-overview'] as const;

export const usePlotOverview = (plotIdArg?: string, isApproved: boolean | 'all' = true) => {
  const plotId = usePlotId();
  const plot_id = plotIdArg ?? plotId;

  assertIsDefined(plot_id, 'plot_id');

  const queryParams: GetPlotOverviewRequestQueryParams =
    isApproved === 'all'
      ? { plot_id }
      : {
          plot_id,
          is_approved: isApproved,
          are_media_files_approved: isApproved,
        };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_PLOT_OVERVIEW_PATH, undefined, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: [...PLOT_OVERVIEW_QUERY_KEY, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreatePlotOverview = (plotIdArg?: string) => {
  const queryClient = useQueryClient();
  const plotId = usePlotId();
  const postPlotOverviewMutation = usePostPlotOverview();

  const submit = async (
    data: PostPlotOverviewRequestBodyData,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    if (data.is_approved) {
      throw new Error('Plot overview approval can only be done through the staff portal');
    }

    const plot_id = plotIdArg ?? plotId;

    assertIsDefined(plot_id, 'plot_id');

    await postPlotOverviewMutation.mutateAsync(
      {
        pathVariables: undefined,
        queryParams: undefined,
        bodyData: {
          ...data,
          plot: plot_id,
        },
      },
      {
        onSuccess: () => {
          queryClient.resetQueries({ queryKey: PLOT_OVERVIEW_QUERY_KEY });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  const isSubmitting = postPlotOverviewMutation.isPending;

  return { submit, isSubmitting };
};

export const useUpdatePlotOverview = () => {
  const patchPlotOverviewMutation = usePatchPlotOverview();
  const queryClient = useQueryClient();

  const submit = async (
    plotOverview: Partial<PlotOverview>,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    assertIsDefined(plotOverview.id, 'plotOverview.id');

    const data: PatchPlotOverviewRequestBodyData = {};
    if (plotOverview.title) {
      data.title = plotOverview.title;
    }
    if (plotOverview.description) {
      data.description = plotOverview.description;
    }

    if (!data.title && !data.description) {
      throw new Error('At least one of title or description must be provided to update a plot overview');
    }

    await patchPlotOverviewMutation.mutateAsync(
      {
        pathVariables: {
          id: plotOverview.id,
        },
        queryParams: undefined,
        bodyData: data,
      },
      {
        onSuccess: () => {
          queryClient.resetQueries({ queryKey: PLOT_OVERVIEW_QUERY_KEY });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  const isSubmitting = patchPlotOverviewMutation.isPending;

  return { submit, isSubmitting };
};

export const useRemovePlotOverview = () => {
  const deletePlotOverviewMutation = useDeletePlotOverview();
  const queryClient = useQueryClient();

  const submit = async (plotOverviewId: string, onSuccess?: () => void, onError?: (error: Error) => void) => {
    assertIsDefined(plotOverviewId, 'plotOverviewId');

    await deletePlotOverviewMutation.mutateAsync(
      {
        pathVariables: {
          id: plotOverviewId,
        },
        queryParams: undefined,
      },
      {
        onSuccess: () => {
          queryClient.resetQueries({ queryKey: PLOT_OVERVIEW_QUERY_KEY });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  const isSubmitting = deletePlotOverviewMutation.isPending;

  return { submit, isSubmitting };
};
